// src/components/pages/FoodLoggerPage.jsx
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from '../../components/ui/popover';
import { CalendarIcon, Plus, Trash2, Droplet } from 'lucide-react';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { useToast } from '../../components/ui/use-toast';
import { Input } from '../../components/ui/input';

import FoodSearch from '../food-logger/FoodSearch';
import FoodEntryForm from '../food-logger/FoodEntryForm';

// Pull userPreferences from context
import { useUserPreferences } from '../../contexts/UserPreferencesContext';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'];

// For liters <-> ounces
const OUNCES_PER_LITER = 33.814;
const LITERS_PER_OUNCE = 1 / OUNCES_PER_LITER;

function toOunces(liters) {
  return liters * OUNCES_PER_LITER;
}
function toLiters(oz) {
  return oz * LITERS_PER_OUNCE;
}

const FoodLoggerPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { preferences: userPreferences } = useUserPreferences(); // from context

  const unitLower = userPreferences?.unit?.toLowerCase() || 'imperial';
  const genderLower = userPreferences?.gender?.toLowerCase() || 'male';

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [meals, setMeals] = useState({});
  const [dailyTotals, setDailyTotals] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0
  });
  const [showAddFood, setShowAddFood] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [loading, setLoading] = useState(true);

  // Water states
  const [waterLogs, setWaterLogs] = useState([]);
  const [waterAmount, setWaterAmount] = useState('');
  const [isAddingWater, setIsAddingWater] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────
  const parseAndConvertWaterInput = (value) => {
    const parsed = parseFloat(value) || 0;
    // Internally store as liters
    return unitLower === 'imperial' ? toLiters(parsed) : parsed;
  };

  const displayWaterAmount = (liters) => {
    if (unitLower === 'imperial') {
      return Math.round(toOunces(liters)); // e.g., 1 L => ~34 oz
    }
    // Round liters to 1 decimal
    return Math.round(liters * 10) / 10;
  };

  // Approx. daily water target
  const getDailyWaterTarget = () => {
    // If preferences not loaded, default to 3.7 L for male
    const liters = genderLower === 'male' ? 3.7 : 2.7;
    return unitLower === 'imperial'
      ? Math.round(liters * OUNCES_PER_LITER)
      : liters;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Load meals & water logs whenever user or date changes
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (user) {
      loadMeals();
      loadWaterLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedDate]);

  // ─────────────────────────────────────────────────────────────────────────
  // Meal Logging & Totals
  // ─────────────────────────────────────────────────────────────────────────
  const loadMeals = async () => {
    setLoading(true);
    try {
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);

      const { data, error } = await supabase
        .from('meal_logs')
        .select(`
          *,
          meal_food_entries (
            *,
            food:foods (*)
          )
        `)
        .eq('user_id', user.id)
        .gte('meal_time', startOfDay.toISOString())
        .lte('meal_time', endOfDay.toISOString());
      if (error) throw error;

      // Organize by meal type
      const organized = {};
      MEAL_TYPES.forEach((type) => {
        organized[type] = data.filter((m) => m.meal_type === type);
      });
      setMeals(organized);
      calculateDailyTotals(data);
    } catch (err) {
      console.error('Error loading meals:', err);
      toast({
        title: 'Error',
        description: 'Failed to load meals',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateDailyTotals = (allMeals) => {
    const totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    allMeals.forEach((meal) => {
      meal.meal_food_entries.forEach((entry) => {
        const ratio = entry.serving_size / entry.food.serving_size;
        totals.calories += entry.food.calories * ratio;
        totals.protein += entry.food.protein_g * ratio;
        totals.carbs += entry.food.carbs_g * ratio;
        totals.fat += entry.food.fat_g * ratio;
      });
    });
    setDailyTotals({
      calories: Math.round(totals.calories),
      protein: Math.round(totals.protein),
      carbs: Math.round(totals.carbs),
      fat: Math.round(totals.fat)
    });
  };

  const handleFoodSelect = (food) => {
    setSelectedFood(food);
  };

  const handleFoodSave = async () => {
    setShowAddFood(false);
    setSelectedFood(null);
    toast({
      title: 'Food Added',
      description: 'Your food entry has been saved successfully.'
    });
    await loadMeals(); // refresh
  };

  // Delete single entry => recalc totals
  const handleDeleteEntry = async (mealType, mealLogId, foodEntryId) => {
    try {
      const { error: entryError } = await supabase
        .from('meal_food_entries')
        .delete()
        .eq('id', foodEntryId);
      if (entryError) throw entryError;

      // Check if meal_log is empty => remove entire row
      const mealArr = meals[mealType] || [];
      const meal = mealArr.find((m) => m.id === mealLogId);
      if (!meal) return;

      const remaining = meal.meal_food_entries.filter(
        (e) => e.id !== foodEntryId
      );
      if (remaining.length === 0) {
        const { error: logError } = await supabase
          .from('meal_logs')
          .delete()
          .eq('id', mealLogId);
        if (logError) throw logError;
      }

      // Update local state
      const updatedMeals = {
        ...meals,
        [mealType]: mealArr
          .map((m) => {
            if (m.id === mealLogId) {
              return {
                ...m,
                meal_food_entries: m.meal_food_entries.filter(
                  (e) => e.id !== foodEntryId
                )
              };
            }
            return m;
          })
          .filter((m) => m.meal_food_entries.length > 0)
      };
      setMeals(updatedMeals);

      // Recompute totals
      const flattened = Object.values(updatedMeals).flat();
      calculateDailyTotals(flattened);

      toast({
        title: 'Entry Deleted',
        description: 'Food entry has been removed successfully.'
      });
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete entry. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Water Logging
  // ─────────────────────────────────────────────────────────────────────────
  const loadWaterLogs = async () => {
    if (!user) return;
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);

    try {
      const { data, error } = await supabase
        .from('water_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startOfDay.toISOString())
        .lte('date', endOfDay.toISOString());
      if (error) throw error;

      setWaterLogs(data || []);
    } catch (err) {
      console.error('Error loading water logs:', err);
      toast({
        title: 'Error',
        description: 'Failed to load water logs',
        variant: 'destructive'
      });
    }
  };

  const handleAddWater = async () => {
    if (!waterAmount) return;
    setIsAddingWater(true);
    try {
      const liters = parseAndConvertWaterInput(waterAmount);
      const { error } = await supabase
        .from('water_logs')
        .insert({
          user_id: user.id,
          date: new Date().toISOString(),
          amount: liters
        });
      if (error) throw error;

      toast({
        title: 'Water Added',
        description: `Successfully added ${waterAmount} ${
          unitLower === 'imperial' ? 'oz' : 'L'
        }.`
      });
      setWaterAmount('');
      await loadWaterLogs();
    } catch (err) {
      console.error('Error adding water:', err);
      toast({
        title: 'Error',
        description: 'Failed to add water',
        variant: 'destructive'
      });
    } finally {
      setIsAddingWater(false);
    }
  };

  const handleClearWater = async () => {
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);

    try {
      const { error } = await supabase
        .from('water_logs')
        .delete()
        .eq('user_id', user.id)
        .gte('date', startOfDay.toISOString())
        .lte('date', endOfDay.toISOString());
      if (error) throw error;

      setWaterLogs([]);
      toast({
        title: 'Water Cleared',
        description: `All water logs for ${format(selectedDate, 'PPP')} removed.`
      });
    } catch (err) {
      console.error('Error clearing water logs:', err);
      toast({
        title: 'Error',
        description: 'Failed to clear water logs',
        variant: 'destructive'
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Rendering
  // ─────────────────────────────────────────────────────────────────────────
  const renderMealSection = (mealType) => {
    const mealItems = meals[mealType] || [];
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="capitalize text-lg">{mealType}</CardTitle>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowAddFood(true)}
            className="h-8"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="pt-2">
          {mealItems.map((meal) => (
            <div key={meal.id} className="space-y-2">
              {meal.meal_food_entries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex justify-between items-start border-b pb-2"
                >
                  <div>
                    <p className="font-medium">{entry.food.name}</p>
                    {/* Round to 1 decimal place */}
                    <p className="text-sm text-muted-foreground">
                      {Number(entry.serving_size).toFixed(1)}
                      {entry.serving_unit}
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="text-right">
                      <p className="font-medium">
                        {Math.round(
                          (entry.food.calories * entry.serving_size) /
                            entry.food.serving_size
                        )}{' '}
                        cal
                      </p>
                      <p className="text-sm text-muted-foreground">
                        P:{' '}
                        {Math.round(
                          (entry.food.protein_g * entry.serving_size) /
                            entry.food.serving_size
                        )}
                        g C:{' '}
                        {Math.round(
                          (entry.food.carbs_g * entry.serving_size) /
                            entry.food.serving_size
                        )}
                        g F:{' '}
                        {Math.round(
                          (entry.food.fat_g * entry.serving_size) /
                            entry.food.serving_size
                        )}
                        g
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() =>
                        handleDeleteEntry(mealType, meal.id, entry.id)
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ))}
          {mealItems.length === 0 && (
            <p className="text-center text-muted-foreground text-sm py-4">
              No foods logged for {mealType}
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderWaterSection = () => {
    // Summation
    const totalLiters = waterLogs.reduce((acc, w) => acc + w.amount, 0);
    const totalDisplay = displayWaterAmount(totalLiters);
    const dailyTarget = getDailyWaterTarget();

    return (
      <Card>
        {/* Exactly like a meal card */}
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="capitalize text-lg">Water</CardTitle>
          <div className="flex items-center gap-2">
            <Droplet className="h-5 w-5 text-blue-500" />
            <Input
              type="number"
              step="0.1"
              value={waterAmount}
              onChange={(e) => setWaterAmount(e.target.value)}
              placeholder={unitLower === 'imperial' ? 'Enter oz' : 'Enter L'}
              className="w-20"
            />
            <Button onClick={handleAddWater} disabled={isAddingWater}>
              {isAddingWater ? 'Saving...' : 'Add Water'}
            </Button>
            <Button variant="destructive" onClick={handleClearWater}>
              Clear
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <CardDescription>Today’s total water vs. daily target</CardDescription>
          <div>
            <p className="font-medium">
              Consumed Today: {totalDisplay}
              {unitLower === 'imperial' ? ' oz' : ' L'}
            </p>
            <p className="text-sm text-muted-foreground">
              Daily Target: {dailyTarget}
              {unitLower === 'imperial' ? ' oz' : ' L'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto p-4 max-w-4xl space-y-6">
      {/* Page header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Food Logger</h1>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              {format(selectedDate, 'PPP')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Daily Totals */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Totals</CardTitle>
          <CardDescription>
            Your nutrition totals for {format(selectedDate, 'PPP')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-muted-foreground text-sm">Calories</p>
              <p className="text-2xl font-bold">{dailyTotals.calories}</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground text-sm">Protein</p>
              <p className="text-2xl font-bold">{dailyTotals.protein}g</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground text-sm">Carbs</p>
              <p className="text-2xl font-bold">{dailyTotals.carbs}g</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground text-sm">Fat</p>
              <p className="text-2xl font-bold">{dailyTotals.fat}g</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Water Section */}
      {renderWaterSection()}

      {/* Meal Sections */}
      <div className="grid gap-6">
        {MEAL_TYPES.map((mealType) => renderMealSection(mealType))}
      </div>

      {/* Add Food Dialog */}
      <Dialog open={showAddFood} onOpenChange={setShowAddFood}>
        <DialogContent className="sm:max-w-[500px]">
          {!selectedFood ? (
            <FoodSearch onFoodSelect={handleFoodSelect} />
          ) : (
            <FoodEntryForm
              food={selectedFood}
              onSave={handleFoodSave}
              onCancel={() => setSelectedFood(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FoodLoggerPage;
